import React, { useCallback, useEffect, useRef, useState } from 'react';
import PriceBadge from '../../../components/PriceBadge/PriceBadge';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { fetchBundleById } from '../../../store/slices/user/asyncThunks';
import { selectUser, selectUserActionsPending } from '../../../store/slices/user/slice';
import { notifyError } from '../../../utils/notify';
import {
  Content,
  EditIconWrapper,
  FreePreviewWrapper,
  Header,
  HeaderLeft,
  HeaderRow,
  IconWrapper,
  ListMedia,
  LoaderContainer,
  MediaCountText,
  MediaWrapper,
  NotValidMedia,
  PageContainer,
  PlayIconWrapper,
  StyledImage,
  StyledVideo,
} from './UploadedBundlePage.styles';
import { EditAlbumMediaModal, EditBundleModal, Spinner } from '../../../components';
import { Text14Light, Text18Bold } from '../../../components/utils/Text/Text.styles';
import { ReactComponent as ImageIcon } from '../../../assets/icons/image-04.svg';
import { ReactComponent as VideoRecorderIcon } from '../../../assets/icons/video-recorder.svg';
import { ReactComponent as PlayIcon } from '../../../assets/icons/play-white.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';
import { ALBUM_MEDIA_TYPE, CRM_ORGANIZATION_MEMBER_ROLE, MEDIA_ASSET_SALE_TAG } from '../../../utils/constants';
import PreviewAlbumMediaModal from '../../../components/modals/PreviewAlbumMediaModal/PreviewAlbumMediaModal';
import { getAlbumMediaTags } from '../../../store/slices/influencerRequest/asyncThunks';

const UploadedBundlePage = () => {
  const { bundleId, influencerId } = useParams();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const { fetchBundleById: fetchBundleByIdPending } = useSelector(selectUserActionsPending);
  const previewAlbumMediaModalRef = useRef();
  const editBundleModalRef = useRef();
  const editAlbumMediaModalRef = useRef();
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [editingBundle, setEditingBundle] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);

  useEffect(() => {
    if (bundleId) {
      dispatch(fetchBundleById({ bundleId, ...(influencerId && { influencerId }) }))
        .unwrap()
        .then(({ bundle }) => {
          setSelectedBundle(bundle);
        })
        .catch(err => {
          notifyError(err.message);
        });
    }
  }, [dispatch, bundleId, influencerId]);

  useEffect(() => {
    dispatch(getAlbumMediaTags());
  }, [dispatch]);

  const refetchBundle = useCallback(() => {
    dispatch(fetchBundleById({ bundleId, ...(influencerId && { influencerId }) }))
      .unwrap()
      .then(({ bundle }) => {
        setSelectedBundle(bundle);
      })
      .catch(err => {
        notifyError(err.message);
      });
  }, [dispatch, bundleId, influencerId]);

  const onEditItemClick = useCallback(media => {
    setSelectedMedia(media);
    editAlbumMediaModalRef.current.show();
  }, []);

  return (
    <PageContainer>
      {fetchBundleByIdPending ? (
        <LoaderContainer>
          <Spinner width={120} height={120} />
        </LoaderContainer>
      ) : (
        selectedBundle && (
          <Content>
            <Header>
              <HeaderLeft>
                <Text18Bold>{selectedBundle.name}</Text18Bold>
                <HeaderRow>
                  <ImageIcon color="#9DA4AE" height={18} width={18} />
                  <MediaCountText>
                    {selectedBundle.media.filter(m => m.kind === ALBUM_MEDIA_TYPE.PHOTO).length}
                  </MediaCountText>
                </HeaderRow>
                <HeaderRow>
                  <VideoRecorderIcon color="#9DA4AE" height={18} width={18} />
                  <MediaCountText>
                    {selectedBundle.media.filter(m => m.kind === ALBUM_MEDIA_TYPE.VIDEO).length}
                  </MediaCountText>
                </HeaderRow>
              </HeaderLeft>
              {[
                CRM_ORGANIZATION_MEMBER_ROLE.OWNER,
                CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR,
                CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD,
                CRM_ORGANIZATION_MEMBER_ROLE.CONTENT_MANAGER,
              ].includes(user.role) && (
                <HeaderRow>
                  <IconWrapper
                    onClick={() => {
                      setEditingBundle(selectedBundle);
                      editBundleModalRef.current.show();
                    }}>
                    <EditIcon />
                  </IconWrapper>
                </HeaderRow>
              )}
            </Header>
            <ListMedia>
              {selectedBundle.representativeMedia && (
                <MediaWrapper
                  onClick={() => {
                    setSelectedMedia({
                      url: selectedBundle.representativeMedia.url,
                      kind: selectedBundle.representativeMedia.type,
                    });
                    previewAlbumMediaModalRef.current.show();
                  }}>
                  {selectedBundle.representativeMedia.type === ALBUM_MEDIA_TYPE.VIDEO ? (
                    <StyledVideo src={selectedBundle.representativeMedia.url} />
                  ) : (
                    <StyledImage src={selectedBundle.representativeMedia.url} />
                  )}
                  {selectedBundle.representativeMedia.type === ALBUM_MEDIA_TYPE.VIDEO && (
                    <PlayIconWrapper>
                      <PlayIcon fill="white" />
                    </PlayIconWrapper>
                  )}
                  <FreePreviewWrapper>FREE PREVIEW</FreePreviewWrapper>
                </MediaWrapper>
              )}
              {selectedBundle.media.map(media => {
                const notValid = !media.fileId;

                return (
                  <MediaWrapper
                    key={media.id}
                    onClick={() => {
                      if (!notValid) {
                        setSelectedMedia(media);
                        previewAlbumMediaModalRef.current.show();
                      }
                    }}>
                    <StyledImage src={media.thumbnailUrl} />
                    <PriceBadge cost={media.cost} free={media.tags?.includes(MEDIA_ASSET_SALE_TAG.SINGLE_FREE)} />
                    {notValid && (
                      <NotValidMedia>
                        <Text14Light>Processing, please wait...</Text14Light>
                      </NotValidMedia>
                    )}
                    {media.kind === ALBUM_MEDIA_TYPE.VIDEO && (
                      <PlayIconWrapper>
                        <PlayIcon fill="white" />
                      </PlayIconWrapper>
                    )}
                    <EditIconWrapper
                      onClick={e => {
                        onEditItemClick(media);
                        e.stopPropagation();
                      }}>
                      <EditIcon color="#fff" />
                    </EditIconWrapper>
                  </MediaWrapper>
                );
              })}
            </ListMedia>
          </Content>
        )
      )}
      <PreviewAlbumMediaModal
        modalRef={previewAlbumMediaModalRef}
        type={selectedMedia?.kind}
        url={selectedMedia?.url}
        setSelectedUrl={setSelectedMedia}
      />
      <EditBundleModal
        modalRef={editBundleModalRef}
        selectedBundle={editingBundle}
        setSelectedBundle={setEditingBundle}
        influencerId={influencerId}
        onSuccess={refetchBundle}
      />
      <EditAlbumMediaModal
        modalRef={editAlbumMediaModalRef}
        selectedMedia={selectedMedia}
        setSelectedMedia={setSelectedMedia}
        influencerId={influencerId}
        onSuccess={refetchBundle}
      />
    </PageContainer>
  );
};

export default UploadedBundlePage;
